import { useChatConnection } from '../lib/chat-realtime.ts';
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Menu, Moon, Search, Sun } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { SidebarState, ThemeChoice } from "@till-erp/platform/user-values";
import {
  apiFetch,
  newIdempotencyKey,
  type Me,
  type OpsRestore,
  type OpsRestoreRun,
} from "../lib/api.ts";
import { useBusinessNow } from "../lib/business-time.ts";
import { formatWeekdayDateTime, greeting } from "../lib/format.ts";
import {
  usePageTitle,
  useProfileSidebar,
  usePublishPageTitle,
  useScreen,
} from "../lib/layout.ts";
import { useTrackRecent } from "../lib/recent.ts";
import { useSession } from "../lib/session.tsx";
import { useProfileTheme, useTheme } from "../lib/theme.ts";
import { cn } from "../lib/utils.ts";
import { buttonClass } from "./ui/variants.ts";
import { AskTillButton, AskTillHost } from "./ask-till.tsx";
import { ClockChip } from "./clock-chip.tsx";
import {
  GlobalSearchHost,
  openGlobalSearch,
  useSearchShortcut,
} from "./global-search.tsx";
import { NavDrawer } from "./nav/nav-drawer.tsx";
import { usePathname } from "./nav/nav-parts.tsx";
import { Sidebar } from "./nav/sidebar.tsx";
import { NotificationBell } from "./notification-bell.tsx";
import { SystemStatusButton } from "./system-status.tsx";
import { NewMenu, S3Header } from "./s3-header.tsx";
import { Tooltip } from "./ui/tooltip.tsx";
import { OPERATIONS_PATH } from "../settings-pages.ts";
import { staffNewMenu } from "../navigation.ts";

export const TRIAL_BANNER_TEXT =
  "Probephase – nichts geht an Kunden, Bank oder DHL";

/** Probephase-Band: volle Breite, 32 px, nicht schließbar. */
export function TrialBanner() {
  return (
    <div
      role="status"
      // Redesign 31.10.2026: ruhiges Band (Seitenfarbe, feine Linie) mit Bernstein-Punkt statt gelber Fläche.
      className="type-meta fixed inset-x-0 top-0 z-20 flex h-8 items-center justify-center gap-space-2 border-b border-line bg-surface-side px-4 text-center text-ink-2"
    >
      <span aria-hidden className="size-1.5 flex-none rounded-full bg-check" />
      {TRIAL_BANNER_TEXT}
    </div>
  );
}

/** Text des Testuhr-Bands, z. B. „Testuhr: Mo 01.03.2027, 07:00 – nur Testumgebung“. */
export function testClockBannerText(now: Date): string {
  return `Testuhr: ${formatWeekdayDateTime(now)} – nur Testumgebung`;
}

/**
 * Testuhr-Band (Story 1.17, D8): neutral Tinte auf Hell (Bernstein bleibt der Probephase vorbehalten), volle Breite,
 * 32 px, nicht schließbar; stapelt unter dem Probephase-Band. Läuft im 30-s-Takt mit.
 */
export function TestClockBanner({ now, top }: { now: Date; top: string }) {
  return (
    <div
      // Kein Live-Bereich: die laufende Minute soll Hilfsmittel nicht jede Minute ansagen.
      role="region"
      aria-label="Testuhr"
      className="type-meta fixed inset-x-0 z-20 flex h-8 items-center justify-center bg-ink px-4 text-center text-on-ink tabular-nums"
      style={{ top }}
    >
      {testClockBannerText(now)}
    </div>
  );
}

/** Abfrage des Berichts der Wiederherstellung (Band und Karte teilen sie). */
export const RESTORE_QUERY_KEY = ["ops", "restore"] as const;

/** Bericht der jüngsten Wiederherstellung; fragt alle 15 s nach (auf der Seite Betrieb alle 5 s). */
export function useRestoreReport(refetchMs = 15_000, enabled = true) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: RESTORE_QUERY_KEY,
    queryFn: () => apiFetch<OpsRestore>(getToken, "/api/v1/ops/restore"),
    enabled,
    refetchInterval: refetchMs,
    refetchOnWindowFocus: true,
  });
}

/** Text des Wiederherstellungs-Bands (Story 1.13); `null` = kein Band (keine offene Wiederherstellung). */
export function restoreBannerText(
  run: Pick<OpsRestoreRun, "status" | "failed"> | null | undefined,
): string | null {
  if (!run || run.status === "released") return null;
  if (run.status === "reconciled")
    return "Wiederherstellung – Abgleich fertig, nichts geht hinaus bis „Wieder live schalten“";
  if (run.failed)
    return "Wiederherstellung – Abgleich gescheitert, nichts geht hinaus";
  return "Wiederherstellung – Abgleich läuft, nichts geht hinaus";
}

/**
 * Wiederherstellungs-Band (Story 1.13, AD-19): neutral Tinte auf Hell wie das Testuhr-Band (Bernstein bleibt der
 * Probephase vorbehalten), volle Breite, 32 px, nicht schließbar, mit Link zum Bericht unter Einstellungen › Betrieb.
 * Am Handy kleiner und zweizeilig, damit der ganze Satz in die 32 px passt (nie gekürzt).
 */
export function RestoreBanner({ text, top }: { text: string; top: string }) {
  return (
    <div
      role="region"
      aria-label="Hinweis Wiederherstellung"
      className="type-meta fixed inset-x-0 z-20 flex h-8 items-center justify-center gap-3 bg-ink px-4 text-center text-on-ink max-tablet:text-2xs max-tablet:leading-[1.25]"
      style={{ top }}
    >
      <span>{text}</span>
      <Link
        to={OPERATIONS_PATH}
        className="inline-flex min-h-6 flex-none items-center underline underline-offset-2"
      >
        Zum Bericht
      </Link>
    </div>
  );
}

/**
 * Hell/Dunkel-Knopf (EXPERIENCE §4.1): fester Name „Dunkles Design“ mit `aria-pressed`; nur das Symbol wechselt
 * (Mond ↔ Sonne), Tooltip gleich dem Namen. Ohne eigene Wahl hell; die Wahl wird im Profil gemerkt.
 */
export function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const dark = theme === "dark";
  return (
    <Tooltip label="Dunkles Design" side="bottom">
      <button
        type="button"
        onClick={toggle}
        aria-pressed={dark}
        aria-label="Dunkles Design"
        className="grid size-10 cursor-pointer place-items-center rounded-full bg-card text-ink-2 shadow-card pointer-coarse:size-11"
      >
        {dark ? (
          <Sun size={18} strokeWidth={1.5} aria-hidden />
        ) : (
          <Moon size={18} strokeWidth={1.5} aria-hidden />
        )}
      </button>
    </Tooltip>
  );
}

/**
 * Seitentitel der Detailseiten (Kunde, Artikel, Auftrag; Ticket 1.233): kein sichtbarer Seitenkopf über dem Datensatz-Kopf,
 * dort steht die Pfadzeile. Der Titel bleibt für Hilfsmittel (`h1` nur für Screenreader) und die Handy-Kopfzeile.
 */
export function HiddenPageTitle({ title }: { title: string }) {
  usePublishPageTitle(title);
  return <h1 className="sr-only">{title}</h1>;
}

/**
 * Zweitknopf im Seitenkopf als Link (Ticket 1.130): gleiche Pille wie `Button variant="secondary"` (`buttonClass('secondary')`),
 * führt zu einer vorhandenen Seite. Nie ohne echtes Ziel.
 */
export function HeaderLink({
  to,
  children,
}: {
  to: string;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      // `hover:enabled:` aus `buttonClass` greift an einem Link nicht, daher Hover hier direkt (Ticket 1.271).
      className={cn(buttonClass("secondary"), "min-w-0 hover:bg-hover")}
    >
      {children}
    </Link>
  );
}

export function useGreetingLabel(): string {
  const { me } = useSession();
  const { now } = useBusinessNow();
  const first = me.displayName.trim().split(/\s+/)[0] ?? "";
  return `${greeting(now)}, ${first}`.toUpperCase();
}

/**
 * Kopfzeile am Handy (DESIGN „Kopfzeile Handy“, EXPERIENCE §4.1): oben fixierte Glasleiste 56 px unter den Bändern –
 * Menü-Knopf, Seitentitel (einzeilig gekürzt), Stempel-Chip (nur mit Mitarbeiter-Stammsatz, Ticket 1.275), Lupe, KI-Copilot, Systemstatus und Glocke. „Neu anlegen“ steht seit
 * Story 1.114 ([RUDOLF 13.10.2026]) in der Menü-Schublade statt als runder „+“ in der Kopfzeile.
 */
function PhoneHeader({ mode = "office" }: { mode?: ShellMode }) {
  const title = usePageTitle();
  const [menu, setMenu] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const searchButton = useRef<HTMLButtonElement>(null);
  const drawerId = useId();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const closeMenu = useCallback(() => setMenu(false), []);
  return (
    <>
      <div
        role="banner"
        className={cn(
          "fixed inset-x-0 z-30 flex h-14 items-center gap-2 bg-[var(--glass)] px-4 backdrop-blur-panel",
          scrolled ? "border-b border-line" : "",
        )}
        style={{ top: "var(--banner-h)" }}
      >
        <button
          ref={menuButton}
          type="button"
          onClick={() => setMenu((v) => !v)}
          aria-expanded={menu}
          aria-controls={drawerId}
          aria-label={menu ? "Menü schließen" : "Menü öffnen"}
          className="-ml-1.5 grid size-11 flex-none cursor-pointer place-items-center rounded-full text-ink-2"
        >
          <Menu size={20} strokeWidth={1.5} aria-hidden />
        </button>
        <span
          data-testid="kopfzeile-titel"
          className="type-card-title min-w-0 flex-1 truncate"
        >
          {title}
        </span>
        {/* Ticket 1.95: Packplatz ohne Suche, KI-Copilot, Systemstatus und Glocke; seit Story 22.11 [RUDOLF 29.10.2026] mit
            Stempel-Chip. Rolle Mitarbeiter (Story 22.11): Stempel-Chip und Glocke. */}
        {mode === "packing" ? <ClockChip compact /> : null}
        {mode === "staff" ? (
          <>
            <ClockChip compact />
            <NotificationBell />
          </>
        ) : null}
        {mode !== "office" ? null : (
          <>
            {/* Ticket 1.275 [RUDOLF 25.10.2026]: kleiner Stempel-Chip (Punkt + Zeit) neben dem Titel; Tippen öffnet das Blatt. */}
            <ClockChip compact />
            <div className="flex flex-none items-center gap-2">
              <button
                ref={searchButton}
                type="button"
                onClick={() => openGlobalSearch(searchButton.current)}
                aria-label="Suche"
                className="grid size-11 cursor-pointer place-items-center rounded-full text-ink-2"
              >
                <Search size={19} strokeWidth={1.5} aria-hidden />
              </button>
              <AskTillButton round />
              <SystemStatusButton />
              <NotificationBell />
            </div>
          </>
        )}
      </div>
      {menu ? (
        <NavDrawer id={drawerId} opener={menuButton} onClose={closeMenu} />
      ) : null}
    </>
  );
}

/** Profil-Vorlieben anwenden: Hell/Dunkel und Seitenleiste schmal/breit aus dem Profil (Umschalten speichert dort), Dichte als Merkmal. */
function useProfilePreferences(me: Me) {
  const { getToken, updateMe } = useSession();
  const patch = useCallback(
    (body: { theme?: ThemeChoice; sidebar?: SidebarState }) => {
      apiFetch<Me>(getToken, "/api/v1/me/preferences", {
        method: "PATCH",
        body,
        idempotencyKey: newIdempotencyKey(),
      })
        .then(updateMe)
        .catch(() => {
          // Ansicht bleibt auf diesem Gerät; beim nächsten Mal gilt wieder das Profil.
        });
    },
    [getToken, updateMe],
  );
  const saveTheme = useCallback(
    (theme: ThemeChoice) => patch({ theme }),
    [patch],
  );
  const saveSidebar = useCallback(
    (sidebar: SidebarState) => patch({ sidebar }),
    [patch],
  );
  useProfileTheme(me.theme, saveTheme);
  useProfileSidebar(me.sidebar, saveSidebar);
  useEffect(() => {
    document.documentElement.dataset.density = me.density;
  }, [me.density]);
}

/**
 * App-Hülle (Change 03.10.2026 Design E · final und „Handy und Tablet“; Navigation Design S3, Story 1.77 in
 * `nav/`): Desktop mit Seitenleiste nach Profilwert (schmal/breit), Tablet mit Symbolleiste, Handy mit Kopfzeile und
 * Menü-Schublade. Der Inhalt beginnt links bei
 * `--content-left` (styles.css), die Größe wechselt ohne Neuladen.
 */
export function AppShell({ children }: { children: ReactNode }) {
  useChatConnection();
  const { me } = useSession();
  if (me.role === "packplatz")
    return <PackingStationShell>{children}</PackingStationShell>;
  if (me.role === "mitarbeiter") return <StaffShell>{children}</StaffShell>;
  return <OfficeShell>{children}</OfficeShell>;
}

/** Hülle je Rolle: Büro (alles), Packplatz (Ticket 1.95), Mitarbeiter (Story 22.11). */
type ShellMode = "office" | "packing" | "staff";

/** Bänder oben (Probephase, Testuhr, Wiederherstellung) und Rahmen für Kopfzeile, Leiste und Inhalt. */
function ShellFrame({
  restoreText,
  mode,
  hosts,
  children,
}: {
  restoreText: string | null;
  mode: ShellMode;
  hosts?: ReactNode;
  children: ReactNode;
}) {
  const { me } = useSession();
  const { now, testActive } = useBusinessNow();
  const screen = useScreen();
  useProfilePreferences(me);
  // „Zuletzt geöffnet“ in der Seitenleiste: geöffnete Datensätze merken – nur Gruppe und Kennung, keine Namen (Story 26.9).
  useTrackRecent(me.id, usePathname());
  const bannerHeight =
    (me.trialPhase ? 32 : 0) + (testActive ? 32 : 0) + (restoreText ? 32 : 0);
  return (
    <div
      data-screen={screen}
      style={{ ["--banner-h" as string]: `${bannerHeight}px` }}
    >
      {me.trialPhase ? <TrialBanner /> : null}
      {testActive ? (
        <TestClockBanner now={now} top={me.trialPhase ? "32px" : "0px"} />
      ) : null}
      {restoreText ? (
        <RestoreBanner
          text={restoreText}
          top={`${(me.trialPhase ? 32 : 0) + (testActive ? 32 : 0)}px`}
        />
      ) : null}
      {/* Neu aufgebaut beim Größenwechsel: eine offene Tablet-Überlagerung bleibt nicht stehen. */}
      {screen === "phone" ? (
        <PhoneHeader mode={mode} />
      ) : (
        <Sidebar key={screen} tablet={screen === "tablet"} />
      )}
      {/* Volle Breite neben der Seitenleiste, keine Höchstbreite. */}
      <main
        data-testid="content"
        className="min-w-0 pr-[var(--content-gutter)] pb-12 pl-[var(--content-left)]"
        style={{
          paddingTop:
            "calc(var(--banner-h) + var(--header-h) + var(--page-top))",
        }}
      >
        {screen === "phone" || mode === "packing" ? null : mode === "staff" ? (
          <StaffHeader />
        ) : (
          <S3Header />
        )}
        {children}
      </main>
      {hosts}
    </div>
  );
}

function OfficeShell({ children }: { children: ReactNode }) {
  // Strg K überall (Story 18.13): Fokus in die Suchpille, sonst schwebende Suche bzw. Vollbild am Handy.
  useSearchShortcut();
  const restoreText = restoreBannerText(useRestoreReport().data?.restore);
  return (
    <ShellFrame
      restoreText={restoreText}
      mode="office"
      hosts={
        <>
          <GlobalSearchHost />
          <AskTillHost />
        </>
      }
    >
      {children}
    </ShellFrame>
  );
}

/**
 * Hülle der Rolle Packplatz (Ticket 1.95): gleiche Leiste und Bänder, aber ohne Kopfzeile mit Suche, „+ Neu“, Stempel,
 * KI-Copilot, Systemstatus und Glocke, ohne Strg K und ohne Abfrage des Wiederherstellungs-Berichts (Server
 * sperrt diese Routen für die Rolle).
 */
function PackingStationShell({ children }: { children: ReactNode }) {
  return (
    <ShellFrame restoreText={null} mode="packing">
      {children}
    </ShellFrame>
  );
}

/**
 * Kopfzeile der Rolle Mitarbeiter am PC und Tablet (Story 22.11, 23.17): rechts „+ Neu ▾“ nur mit „Aufgabe“, Stempel-Chip
 * und Glocke; ohne Suche, KI-Copilot und Systemstatus (die lesen Aufträge und Kunden; der Server sperrt diese Routen).
 */
function StaffHeader() {
  return (
    <header
      data-testid="kopfzeile"
      className="mb-[var(--page-head-gap)] flex min-w-0 items-center justify-end gap-2"
    >
      <NewMenu items={staffNewMenu} />
      <ClockChip />
      <NotificationBell />
    </header>
  );
}

/**
 * Hülle der Rolle Mitarbeiter (Story 22.11): Leiste mit Start und Zeiterfassung, Kopfzeile nur mit Stempel-Chip und
 * Glocke, ohne Strg K und ohne Abfrage des Wiederherstellungs-Berichts.
 */
function StaffShell({ children }: { children: ReactNode }) {
  return (
    <ShellFrame restoreText={null} mode="staff">
      {children}
    </ShellFrame>
  );
}
